import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CustomEventEmitter, download, storage } from '@/util';

import { KEYS, PREFIX_STRIP_REGEXP } from '../constant';
import { ShortcutService } from '../service';
import type {
  ShortcutCommand,
  ShortcutContextValue,
  ShortcutEvent,
  ShortcutItem,
  ShortcutMap,
  ShortcutNamesapce,
} from '../type';
import { loadShortcut, resolveShortcutMap } from '../util';
import ShortcutContext from './context';

const ShortcutProvider = <
  NS extends Dictionary<string>,
  CMD extends Dictionary<string>,
  SCMP extends ShortcutMap<NS, CMD>,
>({
  children,
  debug = import.meta.env.DEV && import.meta.env.MODE !== 'test',
  defaultShortcut,

  keyInputChordTimeout,
  keyThrottleTime,
  shortcutCommand: _shortcutCommand,
  shortcutNamespace,
}: React.PropsWithChildren<{
  debug?: boolean;
  defaultShortcut: SCMP;
  keyInputChordTimeout?: number;
  keyThrottleTime?: number;
  shortcutCommand: CMD;
  shortcutNamespace: NS;
}>) => {
  const namespaceList = Object.values(shortcutNamespace);
  const [namespace, setNamespace] = useState<ShortcutNamesapce<NS>>(
    namespaceList[0],
  );
  const [shortcutMap, setShortcutMap] = useState(() => {
    const loadedShortcut = loadShortcut<NS, CMD>();
    const resolvedShortcutMap = resolveShortcutMap(
      defaultShortcut,
      loadedShortcut,
    );
    const shortcutMap = {} as SCMP;

    namespaceList.forEach(ns => {
      /* v8 ignore next -- @preserve */
      const defaultShortcutList = defaultShortcut[ns] ?? [];
      /* v8 ignore next -- @preserve */
      const loadedShortcutList = resolvedShortcutMap[ns] ?? [];

      Object.assign(shortcutMap, {
        [ns]: [...defaultShortcutList, ...loadedShortcutList].reduce(
          (acc, cur) => {
            if (acc.commandMap.has(cur.command)) {
              acc.result[acc.commandMap.get(cur.command)!] = cur;

              return acc;
            }

            acc.commandMap.set(cur.command, acc.result.length);
            acc.result.push(cur);

            return acc;
          },
          {
            result: [] as ShortcutItem<CMD>[],
            commandMap: new Map<string, number>(),
          },
        ).result,
      });
    });

    return shortcutMap;
  });
  const shortcutServiceRef = useRef(new ShortcutService(debug));

  const shortcutList = useMemo(
    () =>
      shortcutMap[namespace].map(item => ({
        ...item,
        fullKeyText: item.key.map(k => k.replace(PREFIX_STRIP_REGEXP, '')),
      })),
    [namespace, shortcutMap],
  );
  const emitter = useRef(new CustomEventEmitter<ShortcutEvent<NS, CMD>>());
  const setAtShortcutItemInNamespace = useCallback(
    (
      namespace: ShortcutNamesapce<NS>,
      index: number,
      newShortcutItem: Partial<ShortcutItem<CMD>>,
    ) => {
      const newShortcutMap = {
        ...shortcutMap,
        [namespace]: shortcutMap[namespace].map((v, i) =>
          i !== index ? v : { ...v, ...newShortcutItem },
        ),
      };
      importShortcut(newShortcutMap);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shortcutMap],
  );
  const findCommandIndexInNamespace = (
    namespace: ShortcutNamesapce<NS>,
    command: ShortcutCommand<CMD>,
  ) => shortcutMap[namespace].findIndex(v => v.command === command);

  const activate: ShortcutContextValue<NS, CMD>['activate'] = (
    namespace,
    command,
  ) => {
    const index = findCommandIndexInNamespace(namespace, command);

    if (index === -1 || !shortcutMap[namespace][index].inactivated) {
      return;
    }

    setAtShortcutItemInNamespace(namespace, index, { inactivated: false });
  };
  const exportShortcut = useCallback(() => {
    const shortcutJSON = JSON.stringify(shortcutMap, null, 2);
    const shortcutJSONFile = new Blob([shortcutJSON], {
      type: 'application/json',
    });

    download(shortcutJSONFile, `vuex_shortcut_${Date.now()}.json`);
  }, [shortcutMap]);
  const importShortcut = (importedShortcutMap: ShortcutMap<NS, CMD>) => {
    storage.local.set(KEYS.STORAGE_CUSTOM_SHORTCUT, importedShortcutMap);

    const newShortcutMap = { ...defaultShortcut } as SCMP;

    namespaceList.forEach(ns => {
      importedShortcutMap[ns].forEach(v => {
        const index = findCommandIndexInNamespace(ns, v.command);

        /* v8 ignore next -- @preserve */
        if (index === -1) return;

        newShortcutMap[ns][index] = v;
      });
    });

    setShortcutMap(newShortcutMap);
  };
  const inactivate: ShortcutContextValue<NS, CMD>['activate'] = (
    namespace,
    command,
  ) => {
    const index = findCommandIndexInNamespace(namespace, command);

    /* v8 ignore next -- @preserve */
    if (index === -1 || shortcutMap[namespace][index].inactivated) return;

    /* v8 ignore next -- @preserve */
    setAtShortcutItemInNamespace(namespace, index, { inactivated: true });
  };

  const register: ShortcutContextValue<NS, CMD>['register'] = (
    command,
    handler,
  ) => {
    unregister(command);

    emitter.current.on(command, handler);
  };
  const run: ShortcutContextValue<NS, CMD>['run'] = command => {
    emitter.current.emit(command);
  };
  const switchNamespace: ShortcutContextValue<
    NS,
    CMD
  >['switchNamespace'] = namespace => {
    setNamespace(namespace);
  };
  const restore: ShortcutContextValue<NS, CMD>['restore'] = (
    namespace,
    command,
  ) => {
    const index = findCommandIndexInNamespace(namespace, command);
    const dIndex = defaultShortcut[namespace].findIndex(
      v => v.command === command,
    );

    if (
      index === -1 ||
      isEqual(
        shortcutMap[namespace][index].key,
        defaultShortcut[namespace][dIndex].key,
      )
    )
      return;

    setAtShortcutItemInNamespace(
      namespace,
      index,
      defaultShortcut[namespace][dIndex],
    );
  };
  const unregister: ShortcutContextValue<NS, CMD>['unregister'] = command => {
    emitter.current.off(command);
  };
  const changeShortcut = (
    command: ShortcutCommand<CMD>,
    key: string,
    keyIndex: number,
  ) => {
    const index = findCommandIndexInNamespace(namespace, command);

    /* v8 ignore start */
    if (index === -1) return;
    /* v8 ignore end */

    const orgKey = shortcutMap[namespace][index].key;
    const nextKey = orgKey
      .map((v, i) => (i === keyIndex ? key : v))
      .filter(v => !!v);

    setAtShortcutItemInNamespace(namespace, index, { key: nextKey });
  };
  const addShortcutKey = (command: ShortcutCommand<CMD>, key: string) => {
    const index = findCommandIndexInNamespace(namespace, command);

    /* v8 ignore start */
    if (index === -1) return;
    /* v8 ignore end */

    const orgKey = shortcutMap[namespace][index].key;
    const nextKey = [...orgKey, key];

    setAtShortcutItemInNamespace(namespace, index, {
      key: nextKey,
    });
  };
  const isDuplicateShortcut = (
    namespace: ShortcutNamesapce<NS>,
    key: string,
  ) => {
    const loadedShortcut = loadShortcut<NS, CMD>();
    const shortcutMap = resolveShortcutMap(defaultShortcut, loadedShortcut);

    return shortcutMap?.[namespace].some(v => v.key.includes(key)) ?? false;
  };
  const reset: ShortcutContextValue<NS, CMD>['reset'] = () => {
    storage.local.remove(KEYS.STORAGE_CUSTOM_SHORTCUT);
    setShortcutMap(defaultShortcut);
  };

  useEffect(() => {
    shortcutList?.forEach(item => {
      const lowerKey = item.key.map(k =>
        k.replace(PREFIX_STRIP_REGEXP, '').toLowerCase(),
      );

      lowerKey.forEach(key => {
        shortcutServiceRef.current.register([key], () => {
          if (item.inactivated) return;

          emitter.current.emit(item.command);
        });
      });
    });
  }, [shortcutList]);

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      emitter.current.disposeEvent();
    };
  }, [namespace]);

  useEffect(() => {
    shortcutServiceRef.current = new ShortcutService(debug);

    return () => {
      shortcutServiceRef.current.cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    shortcutServiceRef.current.debug = debug;

    if (keyInputChordTimeout)
      ShortcutService.CHORD_TIMEOUT = keyInputChordTimeout;
    if (keyThrottleTime) ShortcutService.THROTTLE_TIME = keyThrottleTime;
  }, [debug, keyInputChordTimeout, keyThrottleTime]);

  return (
    <ShortcutContext.Provider<ShortcutContextValue<NS, CMD>>
      value={{
        namespace,
        namespaceList,
        shortcutList,

        addShortcutKey,
        activate,
        changeShortcut,
        exportShortcut,
        importShortcut,
        inactivate,
        isDuplicateShortcut,
        register,
        reset,
        restore,
        run,
        switchNamespace,
        unregister,
      }}
    >
      {children}
    </ShortcutContext.Provider>
  );
};

ShortcutProvider.displayName = 'ShortcutProvider';

export default ShortcutProvider;
